import type React from 'react';

import { IS_FIREFOX } from 'src/helpers/environment';

/**
 * Props for the `<a>` inside a void chip that opens a link, such as a link or skill chip.
 *
 * Firefox cannot focus an anchor inside contenteditable, so there the anchor leaves the tab
 * order and the chip's wrapper takes its role (see `getLinkChipWrapperAttributes`).
 */
export const LINK_CHIP_ANCHOR_PROPS = {
  'aria-hidden': IS_FIREFOX || undefined,
  // Slate redirects focus to its editable root in Firefox unless the link owns this event.
  onFocus: (event: React.FocusEvent) => event.stopPropagation(),
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') event.stopPropagation();
  },
  tabIndex: IS_FIREFOX ? -1 : 0,
};

/** Wrapper attributes that let Firefox reach the chip's link with Tab and open it with Enter. */
export function getLinkChipWrapperAttributes(label: string) {
  if (!IS_FIREFOX) return {};
  return { 'aria-label': label, onKeyDown: activateLinkFromWrapper, role: 'link', tabIndex: 0 };
}

/** A primary click with no modifier, which the chip handles itself rather than the browser. */
export function getIsPlainLeftClick(event: React.MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function activateLinkFromWrapper(event: React.KeyboardEvent<HTMLElement>) {
  if (event.target !== event.currentTarget || event.key !== 'Enter') return;
  event.preventDefault();
  event.stopPropagation();
  event.currentTarget.querySelector('a')?.click();
}
